import {
	Box,
	Button,
	Drawer,
	DrawerOverlay,
	DrawerContent,
	DrawerCloseButton,
	DrawerHeader,
	DrawerBody,
	Flex,
	Image,
	ModalBody,
	ModalFooter,
	Spinner,
	Tab,
	TabList,
	Tabs,
	Text,
	Tooltip as ChakraTooltip,
	Divider,
	useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@familyzone/component-library";
import { v4 as uuidV4 } from "uuid";
import emptyInbox from "@/assets/emptyInbox.svg?url";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { ClasswizeModal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { colours, qoriaColours } from "@/theme/colours";
import { fonts } from "@/theme/fonts";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import {
	useCreateRulesLibraryListMutation,
	useDeleteRulesLibraryListMutation,
	usePromoteInClassRuleToListMutation,
	useRulesLibraryQuery,
	useRulesLibrarySuggestionsQuery,
	useUpdateRulesLibraryListMutation,
	useValidateRulesLibraryRuleMutation,
} from "@/query/rulesLibraryQuery";
import { RulesLibrarySuggestion, validateRulesLibraryRule } from "@/api/rulesLibrary";
import {
	useApplyClassroomRulesListMutation,
	useClassroomRulesQuery,
	useClearActiveRulesMutation,
	useCreateInClassRuleMutation,
	useDeleteInClassRuleMutation,
	useRemoveClassroomRulesListMutation,
	useUpdateInClassRuleMutation,
} from "@/query/classroomRulesQuery";
import {
	useClassroomAppControlsQuery,
	useCreateAppControlRulesMutation,
	useDeleteAppControlRulesMutation,
	useUpdateAppControlRulesMutation,
} from "@/query/classroomAppControlsQuery";
import { useSignatureStore } from "@/stores/v2/signatureStore";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import useStore from "@/stores";
import { LibraryList } from "./LibraryList";
import { QuickBlock } from "./LibraryList/QuickBlock";
import { ListForm } from "./ListForm";
import { MAX_ACTIVE_RULES, MAX_LISTS, QUICK_BLOCK_LIST_NAME } from "./constants";
import { MaxListsReached } from "./ListForm/Errors/MaxListsReached";
import { QuickBlockListView } from "./QuickBlockListView";
import { ActiveRulesTab } from "./ActiveRules/ActiveRulesTab";
import { ClassRuleForm } from "./ActiveRules/ClassRuleForm";
import { ApplyScope, ClassRulePrefill, InClassRule, NewInClassRule } from "./ActiveRules/types";
import { useRowWrites } from "./ActiveRules/useRowWrites";
import {
	appControlRowIds,
	buildCreateInClassRuleRequest,
	buildPatchInClassRuleRequest,
	countActiveRules,
	isAppControlRule,
	mergeInClassRules,
	toAppliedList,
} from "./ActiveRules/utils";
import { scrollbarProps } from "./styles";
import { List, Rule, RuleAction } from "./types";
import {
	buildCreateRulesLibraryListRequest,
	buildUpdateRulesLibraryListRequest,
	buildValidateRuleRequest,
	collectRuleFaviconUrls,
	getRulesLibraryValidationCopy,
	isLockedRuleConflictError,
	isQuickBlockList,
	isRuleInert,
	isRulesLibraryValidationError,
	preloadFavicons,
	transformRulesLibraryLists,
} from "./utils";

interface Props {
	isOpen: boolean;
	closeDrawer: () => void;
	/** When set, the drawer is opened from a class and shows the Active Rules tab. */
	classroomName?: string;
	/** Class context only: opens the drawer straight into a new class rule with these values. */
	classRulePrefill?: ClassRulePrefill;
}

export const RulesLibraryDrawer = (props: Props): JSX.Element => {
	const { t } = useTranslation();
	const { isOpen, closeDrawer, classroomName, classRulePrefill } = props;
	const isClassContext = Boolean(classroomName);
	const { errorToast, successToast, warningToast } = useToast();
	const applianceId = useUserSessionStore((state) => state.active_device) ?? "";
	// The classroom-rules endpoints key on the UMS stable ID, while the older app-controls
	// endpoints take the classroom's own ID in the path and the stable ID in the body.
	const classroomStableIdV1 = useSelectedClassroomStore((state) => state.stableIdV1) ?? "";
	const classroomStoreId = useSelectedClassroomStore((state) => state.id) ?? "";
	const classroomSourceName = useSelectedClassroomStore((state) => state.sourceId) ?? "";
	const appControlsEnabled = useStore((state) => state.featureToggles.isNativeAppControlsEnabled());
	const appControlClassroom = useMemo(
		() => ({ classroomId: classroomStoreId, classroomStableIdV1, classroomSourceName }),
		[classroomStoreId, classroomStableIdV1, classroomSourceName]
	);

	const { data, isLoading, isError } = useRulesLibraryQuery(applianceId);
	const { data: suggestionsData } = useRulesLibrarySuggestionsQuery(applianceId);
	const createMutation = useCreateRulesLibraryListMutation(applianceId);
	const updateMutation = useUpdateRulesLibraryListMutation(applianceId);
	const deleteMutation = useDeleteRulesLibraryListMutation(applianceId);
	const promoteInClassRuleMutation = usePromoteInClassRuleToListMutation(applianceId);
	const validateMutation = useValidateRulesLibraryRuleMutation(applianceId);
	const lookupName = useSignatureStore((state) => state.lookupName);
	const findAppControl = useSignatureStore((state) => state.findAppControl);

	const classroomRulesQuery = useClassroomRulesQuery(applianceId, classroomStableIdV1, isClassContext);
	const appControlsQuery = useClassroomAppControlsQuery({
		classroomName: classroomSourceName,
		classroomStableIdV1,
		enabled: isClassContext && appControlsEnabled,
	});
	const applyListMutation = useApplyClassroomRulesListMutation(applianceId, classroomStableIdV1);
	const removeListMutation = useRemoveClassroomRulesListMutation(applianceId, classroomStableIdV1);
	const createInClassRuleMutation = useCreateInClassRuleMutation(applianceId, classroomStableIdV1);
	const updateInClassRuleMutation = useUpdateInClassRuleMutation(applianceId, classroomStableIdV1);
	const deleteInClassRuleMutation = useDeleteInClassRuleMutation(applianceId, classroomStableIdV1);
	const clearActiveRulesMutation = useClearActiveRulesMutation(applianceId, classroomStableIdV1, appControlClassroom);
	const createAppControlMutation = useCreateAppControlRulesMutation(appControlClassroom);
	const updateAppControlMutation = useUpdateAppControlRulesMutation(appControlClassroom);
	const deleteAppControlMutation = useDeleteAppControlRulesMutation(appControlClassroom);

	const lists = transformRulesLibraryLists(data?.classroomRulesLists ?? []);
	const quickBlockList = lists.find(isQuickBlockList);
	const atListLimit = lists.length >= MAX_LISTS;
	const showQuickBlock = Boolean(quickBlockList) || !atListLimit;
	const reportedFetchErrorRef = useRef(false);
	const reportedClassroomFetchErrorRef = useRef(false);
	const quickBlockAddInFlightRef = useRef(false);
	const classRuleSaveInFlightRef = useRef(false);
	const applyListInFlightRef = useRef(false);
	const removeListInFlightRef = useRef(false);
	const ruleUpdatesInFlightRef = useRef<Set<string>>(new Set());

	const deleteAllConfirm = useDisclosure();
	const closeDeleteAllConfirm = deleteAllConfirm.onClose;
	/** The list currently being edited, or `null` when creating a new list. `undefined` means the form is closed. */
	const [editingList, setEditingList] = useState<List | null | undefined>(undefined);
	const [tabIndex, setTabIndex] = useState(0);
	const [isClassRuleFormOpen, setIsClassRuleFormOpen] = useState(false);
	const [editingInClassRule, setEditingInClassRule] = useState<InClassRule | undefined>(undefined);
	/** Prefill for the class-rule form that is currently open. Cleared on leaving the form so the next Create Rule starts blank. */
	const [activeClassRulePrefill, setActiveClassRulePrefill] = useState<ClassRulePrefill | undefined>(undefined);
	const [applyingListId, setApplyingListId] = useState<string | undefined>(undefined);
	const [removingListId, setRemovingListId] = useState<string | undefined>(undefined);
	/** Active Rules rows whose toggle write is in flight. Other rows stay clickable, so there can be several. */
	const [updatingRuleIds, setUpdatingRuleIds] = useState<string[]>([]);
	/** Active Rules rows whose delete is in flight, and the rows whose add-to-list is in flight. */
	const { ids: deletingRuleIds, begin: beginRuleDelete, track: trackRuleDelete, reset: resetRuleDeletes } = useRowWrites();
	const { ids: addingToListRuleIds, begin: beginRuleListAdd, track: trackRuleListAdd, reset: resetRuleListAdds } = useRowWrites();
	/** Library lists applied to this class (`appliedLists` on GetClassroomRulesResponse). */
	const appliedListSource = classroomRulesQuery.data?.appliedLists;
	const appliedLists = useMemo(() => (appliedListSource ?? []).map(toAppliedList), [appliedListSource]);
	const appControlRows = useMemo(
		() => (appControlsEnabled ? (appControlsQuery.data ?? []) : []),
		[appControlsEnabled, appControlsQuery.data]
	);
	const inClassRules = mergeInClassRules(classroomRulesQuery.data?.inClassRules ?? [], appControlRows);
	const activeClassroomRules: Rule[] = [
		...inClassRules
			.filter((rule) => !isAppControlRule(rule))
			.map((rule) => ({ id: rule.id, target: rule.target, type: rule.type as "URL" | "SIGNATURE", action: rule.action })),
		...appliedLists.flatMap((list) => list.rules),
	];
	const activeRulesCount = countActiveRules(inClassRules, appliedLists);
	const hasInertActiveRules = [...inClassRules, ...appliedLists.flatMap((list) => list.rules)].some(isRuleInert);
	const isFormOpen = editingList !== undefined || isClassRuleFormOpen;
	const openList = editingList ? (lists.find((list) => list.id === editingList.id) ?? editingList) : editingList;
	const isQuickBlockView = openList != null && isQuickBlockList(openList);
	const isSaving = createMutation.isLoading || updateMutation.isLoading;
	const isClassRuleSaving =
		createInClassRuleMutation.isLoading ||
		updateInClassRuleMutation.isLoading ||
		createAppControlMutation.isLoading ||
		updateAppControlMutation.isLoading ||
		deleteAppControlMutation.isLoading ||
		validateMutation.isLoading;
	// A disabled query still reports `isLoading` while it has no data, so `isInitialLoading`
	// (`isLoading && isFetching`) is used to keep a query that never runs out of the spinner.
	const isActiveRulesLoading = classroomRulesQuery.isInitialLoading || (appControlsEnabled && appControlsQuery.isInitialLoading);
	const activeRulesUnavailable = classroomRulesQuery.data === undefined || (appControlsEnabled && appControlsQuery.data === undefined);
	// A fetch error with no cache is the same unavailability Apply List already guards on. Cached data
	// is still shown after a refetch fails, matching each query's own `isError && data === undefined`.
	const isActiveRulesError =
		(classroomRulesQuery.isError && classroomRulesQuery.data === undefined) ||
		(appControlsEnabled && appControlsQuery.isError && appControlsQuery.data === undefined);

	useEffect(() => {
		if (!isOpen) {
			reportedFetchErrorRef.current = false;
			reportedClassroomFetchErrorRef.current = false;
			classRuleSaveInFlightRef.current = false;
			applyListInFlightRef.current = false;
			removeListInFlightRef.current = false;
			ruleUpdatesInFlightRef.current.clear();
			setUpdatingRuleIds([]);
			resetRuleDeletes();
			resetRuleListAdds();
			setTabIndex(0);
			setIsClassRuleFormOpen(false);
			setEditingInClassRule(undefined);
			setActiveClassRulePrefill(undefined);
			setApplyingListId(undefined);
			setRemovingListId(undefined);
			closeDeleteAllConfirm();
			return;
		}

		if (isError && !reportedFetchErrorRef.current) {
			reportedFetchErrorRef.current = true;
			errorToast({
				title: t("rulesLibrary.failedFetchTitle"),
				description: t("errors.unexpectedError"),
				isClosable: true,
			});
		}

		if (!isError) {
			reportedFetchErrorRef.current = false;
		}
	}, [isOpen, isError, errorToast, closeDeleteAllConfirm, resetRuleDeletes, resetRuleListAdds, t]);

	useEffect(() => {
		if (!isOpen || !isClassContext) {
			reportedClassroomFetchErrorRef.current = false;
			return;
		}

		if (isActiveRulesError && !reportedClassroomFetchErrorRef.current) {
			reportedClassroomFetchErrorRef.current = true;
			errorToast({
				title: t("rulesLibrary.activeRules.failedFetchTitle"),
				description: t("errors.unexpectedError"),
				isClosable: true,
			});
		}

		if (!isActiveRulesError) {
			reportedClassroomFetchErrorRef.current = false;
		}
	}, [isOpen, isClassContext, isActiveRulesError, errorToast, t]);

	useEffect(() => {
		if (!isOpen || !isClassContext || !classRulePrefill) {
			return;
		}

		setEditingList(undefined);
		setEditingInClassRule(undefined);
		setActiveClassRulePrefill(classRulePrefill);
		setIsClassRuleFormOpen(true);
	}, [isOpen, isClassContext, classRulePrefill]);

	// The drawer stays mounted for the class visit, and a co-teacher's writes update only their own
	// cache. Refetch active rules when this teacher opens the drawer so they see that newer state.
	const wasDrawerOpenRef = useRef(isOpen);
	const refetchClassroomRules = classroomRulesQuery.refetch;
	const refetchAppControls = appControlsQuery.refetch;
	useEffect(() => {
		const opened = isOpen && !wasDrawerOpenRef.current;
		wasDrawerOpenRef.current = isOpen;
		if (!opened || !isClassContext) {
			return;
		}

		void refetchClassroomRules();
		if (appControlsEnabled) {
			void refetchAppControls();
		}
	}, [isOpen, isClassContext, appControlsEnabled, refetchClassroomRules, refetchAppControls]);

	// Preload favicons for the rules in the drawer
	useEffect(() => {
		const { getFavicon, findAppControl } = useSignatureStore.getState();
		preloadFavicons(
			collectRuleFaviconUrls(
				[
					...transformRulesLibraryLists(data?.classroomRulesLists ?? []).flatMap((list) => list.rules),
					...mergeInClassRules(classroomRulesQuery.data?.inClassRules ?? [], appControlRows),
					...(suggestionsData?.rulesLibrarySuggestions ?? []),
				],
				{
					getFavicon,
					findAppControlFavicon: (id) => findAppControl(id)?.favicon || undefined,
				}
			)
		);
	}, [data, classroomRulesQuery.data, appControlRows, suggestionsData]);

	// Opens the library list form for a new list (`null` vs `undefined` is "create" vs "closed").
	function handleCreateList() {
		setEditingList(null);
	}

	// Opens the library list form with an existing list.
	function handleEditList(list: List) {
		setEditingList(list);
	}

	// Deletes a library list from My Lists (not an applied classroom list).
	function handleDeleteList(id: string) {
		deleteMutation.mutate(id, {
			onError: () => {
				errorToast({
					title: t("rulesLibrary.failedDeleteTitle"),
					description: t("errors.unexpectedError"),
					isClosable: true,
				});
			},
		});
	}

	// Leaves any open form (library list or in-class rule) and returns to the drawer tabs.
	function handleBack() {
		classRuleSaveInFlightRef.current = false;
		setEditingList(undefined);
		setIsClassRuleFormOpen(false);
		setEditingInClassRule(undefined);
		setActiveClassRulePrefill(undefined);
	}

	function showClassRuleError(
		error: unknown,
		rules: Rule[] = activeClassroomRules,
		candidate?: Rule,
		scope: "list" | "class" = "class",
		title?: string
	): void {
		if (isRulesLibraryValidationError(error)) {
			showValidationToast(getRulesLibraryValidationCopy({ error, candidate, rules, t, lookupName, scope, title }));
			return;
		}

		errorToast({
			title: title ?? t("rulesLibrary.failedSaveTitle"),
			description: t("errors.unexpectedError"),
			isClosable: true,
		});
	}

	const classRuleMutationOptions = { onError: (error: unknown) => showClassRuleError(error) };

	function findInClassRule(id: string): InClassRule | undefined {
		return inClassRules.find((rule) => rule.id === id);
	}

	// Applies a library list to the current classroom so its rules show under Active Rules.
	function handleApplyList(list: List): void {
		if (applyListInFlightRef.current || applyListMutation.isLoading || getApplyDisabledReason(list) !== undefined) {
			return;
		}

		applyListInFlightRef.current = true;
		setApplyingListId(list.id);
		applyListMutation.mutate(list.id, {
			onError: (error: unknown) => showClassRuleError(error, activeClassroomRules, undefined, "class", t("rulesLibrary.failedApplyTitle")),
			onSettled: () => {
				applyListInFlightRef.current = false;
				setApplyingListId(undefined);
			},
		});
	}

	function getApplyDisabledReason(list: List): string | undefined {
		if (isActiveRulesLoading) {
			return t("rulesLibrary.activeRules.loading");
		}

		if (activeRulesUnavailable) {
			return t("rulesLibrary.activeRules.failedFetchTitle");
		}

		if (activeRulesCount + list.rules.length <= MAX_ACTIVE_RULES) {
			return undefined;
		}

		return t("rulesLibrary.activeRules.applyListLimitTooltip", { max: MAX_ACTIVE_RULES });
	}

	// Unapplies one library list from the classroom without deleting the list itself.
	function handleUnapplyList(id: string): void {
		if (removeListInFlightRef.current || removeListMutation.isLoading) {
			return;
		}

		removeListInFlightRef.current = true;
		setRemovingListId(id);
		removeListMutation.mutate(id, {
			onError: (error: unknown) =>
				showClassRuleError(error, activeClassroomRules, undefined, "class", t("rulesLibrary.failedUnapplyTitle")),
			onSettled: () => {
				removeListInFlightRef.current = false;
				setRemovingListId(undefined);
			},
		});
	}

	// Clears applied lists, in-class rules, and app-control rows on this classroom.
	function handleClearActiveRules(): void {
		deleteAllConfirm.onClose();

		if (clearActiveRulesMutation.isLoading) {
			return;
		}

		// App controls are stored separately and are untouched by the clear endpoint, so the mutation
		// deletes their rows too to leave Active Rules genuinely empty.
		clearActiveRulesMutation.mutate(
			{ appControlRowIds: appControlRowIds(inClassRules.filter(isAppControlRule)) },
			classRuleMutationOptions
		);
	}

	// Locks one Active Rules row. Returns false when that row already has a write in flight.
	function beginRuleUpdate(id: string): boolean {
		if (ruleUpdatesInFlightRef.current.has(id)) {
			return false;
		}

		ruleUpdatesInFlightRef.current.add(id);
		setUpdatingRuleIds((ids) => [...ids, id]);
		return true;
	}

	// The write is tracked through its own promise rather than through mutate callbacks, because a
	// mutation hook follows only its newest call: toggling a second row would otherwise drop the
	// first row's callbacks and leave it locked with its error unreported.
	function trackRuleUpdate(id: string, write: Promise<unknown>): void {
		void write
			.catch((error: unknown) => showClassRuleError(error))
			.finally(() => {
				ruleUpdatesInFlightRef.current.delete(id);
				setUpdatingRuleIds((ids) => ids.filter((ruleId) => ruleId !== id));
			});
	}

	// Block/Allow toggle on an Active Rules row (used when blocking-rules-only is off, or for leftover allow rules).
	function handleChangeInClassAction(id: string, action: RuleAction): void {
		const rule = findInClassRule(id);
		if (!rule || rule.action === action || !beginRuleUpdate(id)) {
			return;
		}

		if (isAppControlRule(rule)) {
			trackRuleUpdate(id, updateAppControlMutation.mutateAsync({ rows: rule.appControlRows ?? [], action }));
			return;
		}

		trackRuleUpdate(id, updateInClassRuleMutation.mutateAsync({ ruleId: id, body: buildPatchInClassRuleRequest(rule, { action }) }));
	}

	// On/Off toggle on an Active Rules row. Only the blocking-rules-only UI calls this.
	function handleChangeInClassEnabled(id: string, enabled: boolean): void {
		const rule = findInClassRule(id);
		if (!rule) {
			return;
		}

		if (isAppControlRule(rule)) {
			// App-control storage has no enabled flag, so On/Off is expressed as block/allow only,
			// which makes an unchanged action the same no-op that handleChangeInClassAction guards.
			const appControlAction = enabled ? "block" : "allow";
			if (rule.action === appControlAction || !beginRuleUpdate(id)) {
				return;
			}

			trackRuleUpdate(id, updateAppControlMutation.mutateAsync({ rows: rule.appControlRows ?? [], action: appControlAction }));
			return;
		}

		// Switching a rule on in blocking-rules-only mode also makes it a block rule, matching the
		// legacy policy drawer where On maps to blocked and Off only clears the enabled flag.
		const body = buildPatchInClassRuleRequest(rule, enabled ? { enabled, action: "block" } : { enabled });
		// An empty body means the rule already matches what the teacher clicked, so there is nothing to send.
		if (Object.keys(body).length === 0 || !beginRuleUpdate(id)) {
			return;
		}

		trackRuleUpdate(id, updateInClassRuleMutation.mutateAsync({ ruleId: id, body }));
	}

	// Saves audience from the in-class rule form (whole class vs specific students) and closes the form.
	function handleChangeInClassApplyTo(id: string, applyTo: ApplyScope, studentIds: string[]): void {
		const rule = findInClassRule(id);
		if (!rule) {
			return;
		}

		if (isAppControlRule(rule)) {
			const row = rule.appControlRows?.[0];
			if (!row) {
				return;
			}

			updateAppControlMutation.mutate(
				{
					rows: [
						{
							...row,
							isClassroomRule: applyTo === "class",
							studentStableIdV1: applyTo === "class" ? undefined : studentIds[0],
						},
					],
					action: rule.action,
				},
				{ onSuccess: handleBack, onError: (error: unknown) => showClassRuleError(error) }
			);
			return;
		}

		updateInClassRuleMutation.mutate(
			{ ruleId: id, body: buildPatchInClassRuleRequest(rule, { applyTo, studentIds }) },
			{ onSuccess: handleBack, onError: (error: unknown) => showClassRuleError(error) }
		);
	}

	// Opens a blank in-class rule form (`undefined` editing rule means create).
	function handleCreateInClassRule(): void {
		setEditingInClassRule(undefined);
		setIsClassRuleFormOpen(true);
	}

	// Creates a new in-class website/signature rule or app-control rule, then closes the form.
	// Website and signature rules are checked against school filter locked rules first. A locked-rule
	// overlap warns the teacher and the rule is still created. Any other check failure stops the save.
	async function handleSaveInClassRule(saved: NewInClassRule): Promise<void> {
		if (isClassRuleSaving || classRuleSaveInFlightRef.current) {
			return;
		}

		if (saved.type === "APP_CONTROL" && !appControlsEnabled) {
			return;
		}

		classRuleSaveInFlightRef.current = true;
		const onSuccess = () => {
			classRuleSaveInFlightRef.current = false;
			handleBack();
		};
		const onError = (error: unknown, rules: Rule[] = activeClassroomRules, candidate?: Rule): void => {
			classRuleSaveInFlightRef.current = false;
			showClassRuleError(error, rules, candidate);
		};

		if (saved.type === "APP_CONTROL") {
			createAppControlMutation.mutate(
				{
					appControlId: saved.target,
					name: findAppControl(saved.target)?.name ?? saved.target,
					action: saved.action,
					studentStableIdV1s: saved.applyTo === "class" ? [] : saved.studentIds,
				},
				{ onSuccess, onError: (error) => onError(error) }
			);
			return;
		}

		const created = { ...saved, type: saved.type };
		const candidate: Rule = { id: created.id, target: created.target, type: created.type, action: created.action };
		try {
			await validateMutation.mutateAsync(buildValidateRuleRequest(candidate, []));
		} catch (error) {
			if (!classRuleSaveInFlightRef.current) {
				return;
			}
			const lockedRuleWarning = isRulesLibraryValidationError(error) && isLockedRuleConflictError(error);
			if (!lockedRuleWarning) {
				onError(error, [candidate, ...activeClassroomRules], candidate);
				return;
			}
		}

		if (!classRuleSaveInFlightRef.current) {
			return;
		}

		createInClassRuleMutation.mutate(buildCreateInClassRuleRequest(created), {
			onSuccess,
			// The rule is passed along so a validation error naming its ID can be reported against it.
			onError: (error) => onError(error, [candidate, ...activeClassroomRules], candidate),
		});
	}

	// Opens the in-class rule form to edit audience (and related fields) for an existing row.
	function handleEditInClassRule(rule: InClassRule): void {
		setEditingInClassRule(rule);
		setIsClassRuleFormOpen(true);
	}

	// Moves an in-class website/signature rule onto a library list. App-control rules cannot be added to lists.
	function handleAddInClassToList(ruleId: string, listId: string): void {
		const rule = findInClassRule(ruleId);
		if (!rule || isAppControlRule(rule) || !classroomStableIdV1 || !beginRuleListAdd(ruleId)) {
			return;
		}

		const candidate = {
			id: rule.id,
			target: rule.target,
			type: rule.type === "SIGNATURE" ? ("SIGNATURE" as const) : ("URL" as const),
			action: rule.action,
		};
		const listRules = appliedLists.find((list) => list.id === listId)?.rules ?? [];
		trackRuleListAdd(ruleId, promoteInClassRuleMutation.mutateAsync({ listId, body: { ruleId, classId: classroomStableIdV1 } }), (error) =>
			showClassRuleError(error, [candidate, ...listRules], undefined, "list")
		);
	}

	// Removes one Active Rules row. App-control and website/signature rules use different endpoints.
	function handleDeleteInClassRule(id: string): void {
		const rule = findInClassRule(id);
		if (!rule || !beginRuleDelete(id)) {
			return;
		}

		const write = isAppControlRule(rule)
			? deleteAppControlMutation.mutateAsync(appControlRowIds([rule]))
			: deleteInClassRuleMutation.mutateAsync(id);
		trackRuleDelete(id, write, showClassRuleError);
	}

	function showValidationToast(copy: { title: string; description: string; severity: "error" | "warning" }): void {
		const toast = copy.severity === "warning" ? warningToast : errorToast;
		toast({
			title: copy.title,
			description: copy.description,
			isClosable: true,
		});
	}

	// Creates or updates a library list from the list form.
	async function handleSaveList(saved: List): Promise<void> {
		try {
			if (editingList) {
				await updateMutation.mutateAsync({
					listId: editingList.id,
					body: buildUpdateRulesLibraryListRequest(editingList, saved),
				});
			} else {
				await createMutation.mutateAsync(buildCreateRulesLibraryListRequest(saved));
			}
			successToast({
				title: t("rulesLibrary.savedTitle"),
				isClosable: true,
			});
			setEditingList(undefined);
		} catch (error) {
			if (isRulesLibraryValidationError(error)) {
				showValidationToast(
					getRulesLibraryValidationCopy({
						error,
						rules: saved.rules,
						t,
						lookupName,
						title: t("rulesLibrary.failedSaveTitle"),
					})
				);
				return;
			}

			errorToast({
				title: t("rulesLibrary.failedSaveTitle"),
				description: t("errors.unexpectedError"),
				isClosable: true,
			});
		}
	}

	function showQuickBlockSaveSuccess(): void {
		successToast({
			title: t("rulesLibrary.savedTitle"),
			isClosable: true,
		});
	}

	function showQuickBlockSaveError(error: unknown, rules: Rule[] = []): void {
		if (isRulesLibraryValidationError(error)) {
			showValidationToast(
				getRulesLibraryValidationCopy({
					error,
					rules,
					t,
					lookupName,
				})
			);
			return;
		}

		errorToast({
			title: t("rulesLibrary.failedSaveTitle"),
			description: t("errors.unexpectedError"),
			isClosable: true,
		});
	}

	// Adds a suggestion to Quick Block, creating that list on first add.
	function handleQuickBlockAdd(suggestion: RulesLibrarySuggestion) {
		if (quickBlockAddInFlightRef.current || createMutation.isLoading || updateMutation.isLoading) {
			return;
		}
		if (quickBlockList?.rules.some((rule) => rule.target === suggestion.target)) {
			return;
		}
		if (!quickBlockList && atListLimit) {
			return;
		}

		const classroomRule = {
			id: uuidV4(),
			target: suggestion.target,
			type: suggestion.type,
			action: suggestion.action,
		};
		const onSettled = () => {
			quickBlockAddInFlightRef.current = false;
		};

		quickBlockAddInFlightRef.current = true;

		if (quickBlockList) {
			updateMutation.mutate(
				{
					listId: quickBlockList.id,
					body: {
						name: QUICK_BLOCK_LIST_NAME,
						added: [classroomRule],
					},
				},
				{
					onSuccess: showQuickBlockSaveSuccess,
					onError: (error) => showQuickBlockSaveError(error, quickBlockList.rules),
					onSettled,
				}
			);
			return;
		}

		createMutation.mutate(
			{
				name: QUICK_BLOCK_LIST_NAME,
				classroomRules: [classroomRule],
			},
			{
				onSuccess: showQuickBlockSaveSuccess,
				onError: (error) => showQuickBlockSaveError(error),
				onSettled,
			}
		);
	}

	// Removes a rule from Quick Block. Deleting the last rule deletes the list.
	function handleQuickBlockDeleteRule(ruleId: string): void {
		if (!openList) {
			return;
		}

		if (openList.rules.length <= 1) {
			deleteMutation.mutate(openList.id, {
				onSuccess: () => {
					showQuickBlockSaveSuccess();
					handleBack();
				},
				onError: (error) => showQuickBlockSaveError(error, openList.rules),
			});
			return;
		}

		updateMutation.mutate(
			{
				listId: openList.id,
				body: {
					name: QUICK_BLOCK_LIST_NAME,
					deleted: [ruleId],
				},
			},
			{
				onSuccess: showQuickBlockSaveSuccess,
				onError: (error) => showQuickBlockSaveError(error, openList.rules),
			}
		);
	}

	function renderLibraryBody(): JSX.Element {
		return (
			<>
				{showQuickBlock && !isError && !isLoading && (
					<QuickBlock
						suggestions={suggestionsData?.rulesLibrarySuggestions ?? []}
						onAdd={handleQuickBlockAdd}
						addedTargets={quickBlockList?.rules.map((rule) => rule.target)}
						isLoading={createMutation.isLoading || updateMutation.isLoading}
						defaultExpanded={lists.length === 0}
					/>
				)}
				{isLoading ? (
					<Flex justify="center" py={16} aria-label={t("rulesLibrary.loading")}>
						<Spinner />
					</Flex>
				) : isError ? (
					<Flex direction="column" align="center" justify="center" gap={2} py={16}>
						<Text fontSize="sm" color={colours.text.regular}>
							{t("rulesLibrary.failedFetchTitle")}
						</Text>
						<Text fontSize="sm" color={colours.text.regular}>
							{t("errors.unexpectedError")}
						</Text>
					</Flex>
				) : lists.length === 0 ? (
					<Box textAlign="center" mt={50}>
						<Image src={emptyInbox} alt={t("rulesLibrary.emptyAlt")} mx="auto" mb={20} />
						<Text fontSize="sm" color="neutrals.300">
							{t("rulesLibrary.emptyLine1")}
						</Text>
						<Text fontSize="sm" color="neutrals.300">
							{t("rulesLibrary.emptyLine2")}
						</Text>
					</Box>
				) : (
					<>
						<Flex align="center" gap={2} justify="space-between" mb={4}>
							<Text fontSize="md" color={colours.text.regular}>
								{t("rulesLibrary.myLists")}
							</Text>
							<Text fontSize="sm" color={colours.text.regular}>{`${lists.length} / ${MAX_LISTS}`}</Text>
						</Flex>
						<LibraryList
							lists={lists}
							onEdit={handleEditList}
							onDelete={handleDeleteList}
							onApply={isClassContext ? handleApplyList : undefined}
							getApplyDisabledReason={isClassContext ? getApplyDisabledReason : undefined}
							appliedListIds={appliedLists.map((list) => list.id)}
							applyingListId={applyingListId}
						/>
					</>
				)}
			</>
		);
	}

	function renderNewListButton(): JSX.Element {
		return (
			<>
				<Box alignSelf="flex-end">
					<ChakraTooltip label={t("rulesLibrary.libraryMaxTooltip", { max: MAX_LISTS })} isDisabled={!atListLimit} shouldWrapChildren>
						<Button
							onClick={handleCreateList}
							isDisabled={isLoading || isError || atListLimit}
							leftIcon={<ChakraFAIcon icon="fa-plus" variant="solid" boxSize={3.5} />}
						>
							{t("rulesLibrary.newList")}
						</Button>
					</ChakraTooltip>
				</Box>
				{atListLimit && <MaxListsReached count={lists.length} max={MAX_LISTS} />}
			</>
		);
	}

	// The class drawer's library tab keeps New List in the header, alongside the Active Rules toolbar.
	function renderClassLibraryToolbar(): JSX.Element {
		return (
			<Flex direction="column" gap={4} mt={4}>
				{renderNewListButton()}
			</Flex>
		);
	}

	// Delete all / Create Rule live in the drawer header so they stay visible while the rules scroll.
	function renderActiveRulesToolbar(): JSX.Element {
		const isEmpty = inClassRules.length === 0 && appliedLists.length === 0;
		const atRuleLimit = activeRulesCount >= MAX_ACTIVE_RULES;

		return (
			<Flex align="center" justify="flex-end" mt={4} gap={2} wrap="wrap">
				<ChakraTooltip
					label={t("rulesLibrary.activeRules.classRuleLimitTooltip", { max: MAX_ACTIVE_RULES })}
					isDisabled={!atRuleLimit}
					shouldWrapChildren
				>
					<Button
						onClick={handleCreateInClassRule}
						isDisabled={atRuleLimit}
						leftIcon={<ChakraFAIcon icon="fa-plus" variant="solid" boxSize={3.5} />}
						size="sm"
					>
						{t("rulesLibrary.activeRules.createRule")}
					</Button>
				</ChakraTooltip>
				<Button
					variant="dangerOutline"
					onClick={deleteAllConfirm.onOpen}
					isDisabled={isEmpty || clearActiveRulesMutation.isLoading}
					isLoading={clearActiveRulesMutation.isLoading}
					loadingText={t("rulesLibrary.activeRules.deleteAll")}
					leftIcon={<ChakraFAIcon icon="fa-trash-can" variant="solid" boxSize={3.5} />}
					size="sm"
				>
					{t("rulesLibrary.activeRules.deleteAll")}
				</Button>
			</Flex>
		);
	}

	function renderActiveRulesBody(): JSX.Element {
		if (isActiveRulesLoading) {
			return (
				<Flex justify="center" py={16} aria-label={t("rulesLibrary.activeRules.loading")}>
					<Spinner />
				</Flex>
			);
		}

		if (isActiveRulesError) {
			return (
				<Flex direction="column" align="center" justify="center" gap={2} py={16}>
					<Text fontSize="sm" color={colours.text.regular}>
						{t("rulesLibrary.activeRules.failedFetchTitle")}
					</Text>
					<Text fontSize="sm" color={colours.text.regular}>
						{t("errors.unexpectedError")}
					</Text>
				</Flex>
			);
		}

		return (
			<ActiveRulesTab
				inClassRules={inClassRules}
				lists={appliedLists}
				onCreateRule={handleCreateInClassRule}
				onBrowseLibrary={() => setTabIndex(1)}
				onChangeInClassAction={handleChangeInClassAction}
				onChangeInClassEnabled={handleChangeInClassEnabled}
				onAddInClassToList={handleAddInClassToList}
				onEditInClassRule={handleEditInClassRule}
				onDeleteInClassRule={handleDeleteInClassRule}
				onUnapplyList={handleUnapplyList}
				removingListId={removingListId}
				updatingRuleIds={updatingRuleIds}
				deletingRuleIds={deletingRuleIds}
				addingToListRuleIds={addingToListRuleIds}
			/>
		);
	}

	return (
		<Drawer isOpen={isOpen} onClose={closeDrawer} size="lg">
			<DrawerOverlay />
			<DrawerContent>
				<DrawerCloseButton />
				<ErrorBoundary>
					{isFormOpen ? (
						isClassRuleFormOpen ? (
							<ClassRuleForm
								onBack={handleBack}
								rule={editingInClassRule}
								prefill={activeClassRulePrefill}
								appliedLists={appliedLists}
								onCreate={handleSaveInClassRule}
								isSaving={isClassRuleSaving}
								validateRule={(body) => validateRulesLibraryRule(applianceId, body)}
								onSave={
									editingInClassRule
										? (applyTo, studentIds) => {
												handleChangeInClassApplyTo(editingInClassRule.id, applyTo, studentIds);
											}
										: undefined
								}
							/>
						) : isQuickBlockView && openList ? (
							<QuickBlockListView
								list={openList}
								onBack={handleBack}
								onDeleteRule={handleQuickBlockDeleteRule}
								isDeleting={updateMutation.isLoading || deleteMutation.isLoading}
							/>
						) : (
							<ListForm
								list={editingList ?? undefined}
								onBack={handleBack}
								onSave={handleSaveList}
								validateRule={(body) => validateMutation.mutateAsync(body)}
								isSaving={isSaving}
							/>
						)
					) : isClassContext ? (
						<>
							<DrawerHeader>
								<Text fontFamily={fonts.body} fontSize="xl" fontWeight={500} mb={3}>
									{classroomName}
								</Text>
								<Text fontFamily={fonts.body} color={colours.text.paragraph.light} fontSize="md" fontWeight={400}>
									{tabIndex === 0 ? t("rulesLibrary.activeRules.subtitle") : t("rulesLibrary.activeRules.activateHint")}
								</Text>
								<Tabs index={tabIndex} onChange={setTabIndex} w="full" mt={4}>
									<TabList>
										<Tab fontFamily={fonts.body} fontWeight={400}>
											{t("rulesLibrary.activeRules.tab")}
											{hasInertActiveRules && (
												<Box
													as="span"
													display="inline-flex"
													ml={2}
													role="img"
													aria-label={t("rulesLibrary.activeRules.notInEffectAria")}
													bg={qoriaColours.brand.warning[10]}
													borderRadius="full"
													p={1}
												>
													<ChakraFAIcon
														icon="fa-triangle-exclamation"
														variant="solid"
														boxSize={4}
														color={qoriaColours.brand.warning[300]}
													/>
												</Box>
											)}
										</Tab>
										<Tab fontFamily={fonts.body} fontWeight={400}>
											<ChakraFAIcon icon="fa-books" variant="solid" boxSize={3.5} mr={2} />
											{t("rulesLibrary.title")}
										</Tab>
									</TabList>
								</Tabs>
								{tabIndex === 0 ? !isActiveRulesLoading && !isActiveRulesError && renderActiveRulesToolbar() : renderClassLibraryToolbar()}
							</DrawerHeader>
							<DrawerBody sx={scrollbarProps}>{tabIndex === 0 ? renderActiveRulesBody() : renderLibraryBody()}</DrawerBody>
							<ClasswizeModal
								isOpen={deleteAllConfirm.isOpen}
								onClose={deleteAllConfirm.onClose}
								title={t("rulesLibrary.activeRules.deleteAllTitle")}
								size="lg"
								dataTestId="active-rules-delete-all"
							>
								<ModalBody>
									<Text fontFamily={fonts.body} fontWeight={400}>
										{t("rulesLibrary.activeRules.deleteAllBody")}
									</Text>
								</ModalBody>
								<ModalFooter gap={2}>
									<Button variant="ghost" onClick={deleteAllConfirm.onClose}>
										{t("rulesLibrary.deleteListCancel")}
									</Button>
									<Button variant="danger" onClick={handleClearActiveRules}>
										{t("rulesLibrary.activeRules.deleteAllConfirm")}
									</Button>
								</ModalFooter>
							</ClasswizeModal>
						</>
					) : (
						<>
							<DrawerHeader>
								<Flex justifyContent="space-between">
									<Text fontFamily={fonts.body} fontSize="xl" fontWeight={500} mb={3}>
										{t("rulesLibrary.title")}
									</Text>
								</Flex>
								<Flex justifyContent="space-between" align="flex-start">
									<Text fontFamily={fonts.body} color={colours.text.paragraph.light} fontSize="md" fontWeight={400} flex={1} pr="50px">
										{t("rulesLibrary.subtitle")}
									</Text>
									<Box flexShrink={0}>
										<Tooltip label={t("rulesLibrary.libraryMaxTooltip", { max: MAX_LISTS })} isDisabled={!atListLimit}>
											<Button
												onClick={handleCreateList}
												isDisabled={isLoading || isError || atListLimit}
												leftIcon={<ChakraFAIcon icon="fa-plus" variant="solid" boxSize={3.5} />}
											>
												{t("rulesLibrary.newList")}
											</Button>
										</Tooltip>
									</Box>
								</Flex>
								{atListLimit && (
									<Box mt={4}>
										<MaxListsReached count={lists.length} max={MAX_LISTS} />
									</Box>
								)}
								<Divider mt={4} />
							</DrawerHeader>
							<DrawerBody sx={scrollbarProps}>{renderLibraryBody()}</DrawerBody>
						</>
					)}
				</ErrorBoundary>
			</DrawerContent>
		</Drawer>
	);
};
